import { computeComplianceScore } from "../components/ComplianceScoreCard";
import { scoredGoals } from "./goalActivation";

/**
 * Composite launch-readiness score 0-100 blending:
 *   - Plan Health (30%)
 *   - Compliance Risk (25%)
 *   - Roadmap completion % (25%)
 *   - Budget runway (20%)
 *
 * Returns { score, grade, label, breakdown: {health, compliance, roadmap, runway}, missing: [...] }
 * or null if there's not enough data (no plan).
 */
export function computeLaunchScore(business) {
  if (!business?.plan) return null;

  const breakdown = {
    health: null,      // plan health score
    compliance: null,  // compliance risk score
    roadmap: null,     // roadmap completion %
    runway: null,      // budget runway score
    goals: null,       // avg goal progress %
  };
  const missing = [];

  // Plan health
  const hc = business.health_check;
  if (typeof hc?.score === "number") {
    breakdown.health = hc.score;
  } else {
    missing.push("Run plan health check");
  }

  // Compliance
  const cmp = computeComplianceScore(business.compliance);
  if (cmp) {
    breakdown.compliance = cmp.score;
  } else {
    missing.push("Scan compliance");
  }

  // Roadmap completion %
  const roadmap = business.roadmap || [];
  if (roadmap.length > 0) {
    const done = roadmap.filter((r) => r.status === "done").length;
    breakdown.roadmap = Math.round((done / roadmap.length) * 100);
  } else {
    missing.push("Generate roadmap");
  }

  // Budget runway score (runway months: 0 → score 0, 12+ → 100)
  const budget = business.budget;
  if (budget) {
    const startup = (budget.startup_costs || []).reduce((a, l) => a + (Number(l.amount) || 0), 0);
    const burn = (budget.monthly_burn || []).reduce((a, l) => a + (Number(l.amount) || 0), 0);
    if (burn > 0) {
      const runway = startup / burn;
      breakdown.runway = Math.round(Math.min(100, (runway / 12) * 100));
    } else {
      breakdown.runway = 100; // zero burn = infinite runway
    }
  } else {
    missing.push("Plan budget");
  }

  // Goals: average completion % across active goals (excludes at_risk from average but surfaces in missing).
  // Feb 2027 · In Operating Mode, only "activated" Strategic Goals
  // (those with at least one Project attached) count toward the
  // score. Un-activated goals are aspirational placeholders and
  // shouldn't flatter the number. Launching mode is unaffected.
  const goals = scoredGoals(business);
  if (goals.length > 0) {
    const scored = goals
      .filter((g) => g.target && g.target > 0)
      .map((g) => Math.min(100, Math.round(((g.progress || 0) / g.target) * 100)));
    breakdown.goals = scored.length ? Math.round(scored.reduce((a, v) => a + v, 0) / scored.length) : 0;
  } else {
    missing.push("Set business goals");
  }

  // Weighted average over available dimensions; re-normalize weights
  const weights = { health: 0.25, compliance: 0.2, roadmap: 0.2, runway: 0.2, goals: 0.15 };
  let totalWeight = 0;
  let weighted = 0;
  for (const [k, w] of Object.entries(weights)) {
    if (breakdown[k] !== null) {
      totalWeight += w;
      weighted += breakdown[k] * w;
    }
  }
  if (totalWeight === 0) return null;
  const score = Math.round(weighted / totalWeight);

  let grade, label;
  if (score >= 85) { grade = "A"; label = "Launch-ready"; }
  else if (score >= 70) { grade = "B"; label = "Strong"; }
  else if (score >= 55) { grade = "C"; label = "On track"; }
  else if (score >= 40) { grade = "D"; label = "Fragile"; }
  else                  { grade = "F"; label = "Not ready"; }

  return { score, grade, label, breakdown, missing };
}

/** Grade thresholds (score >= threshold gives that grade). */
export const GRADE_THRESHOLDS = { A: 85, B: 70, C: 55, D: 40 };

/** Returns grade letter for a score — stable helper for tier-up detection. */
export function gradeForScore(score) {
  if (score >= 85) return "A";
  if (score >= 70) return "B";
  if (score >= 55) return "C";
  if (score >= 40) return "D";
  return "F";
}

export function launchScoreColor(score) {
  if (score >= 85) return "#059669";
  if (score >= 70) return "#4A4458";
  if (score >= 55) return "#F59E0B";
  if (score >= 40) return "#EA580C";
  return "#DC2626";
}

/**
 * Founder-friendly stage framing of the launch score. Replaces the
 * "Grade F · Not ready" letter-grade in the UI with an encouraging,
 * growth-mindset progression (1 → 5) and a calmer color ramp (the lowest
 * band is brand purple "early-stage", never alarming red).
 */
export function launchStage(score) {
  if (score >= 85) return { step: 5, label: "Launch-ready", color: "#059669" };
  if (score >= 70) return { step: 4, label: "Almost ready", color: "#0D9488" };
  if (score >= 55) return { step: 3, label: "Taking shape", color: "#0284C7" };
  if (score >= 40) return { step: 2, label: "Getting going", color: "#D97706" };
  return { step: 1, label: "Just getting started", color: "#7C5FB0" };
}

// Dimension → the concrete action that lifts it, with the approximate
// points on offer (the dimension's weight). Keeps the "next best move"
// nudge self-contained in the badge — no extra fetch.
const NEXT_MOVE = {
  health: { label: "Run your Plan Health check", hash: "#section-health", pts: 25, roadmap: false },
  compliance: { label: "Scan what licenses you need", hash: "#section-compliance-anchor", pts: 20, roadmap: false },
  roadmap: { label: "Generate your roadmap", hash: "", pts: 20, roadmap: true },
  runway: { label: "Plan your budget & runway", hash: "#section-budget", pts: 20, roadmap: false },
  goals: { label: "Set your business goals", hash: "#section-goals", pts: 15, roadmap: false },
};

const DIM_ORDER = ["health", "compliance", "roadmap", "runway", "goals"];

/**
 * The single highest-leverage next action for a plan: prefer a not-yet-set
 * dimension (biggest weight first), else the lowest-scoring one still below
 * 100. Returns { key, label, hash, roadmap, pts } or null when maxed out.
 */
export function nextBestMove(result) {
  if (!result) return null;
  const { breakdown } = result;
  let pick = DIM_ORDER.filter((k) => breakdown[k] === null).sort(
    (a, b) => NEXT_MOVE[b].pts - NEXT_MOVE[a].pts,
  )[0];
  if (!pick) {
    pick = DIM_ORDER.filter((k) => breakdown[k] !== null && breakdown[k] < 100).sort(
      (a, b) => breakdown[a] - breakdown[b] || NEXT_MOVE[b].pts - NEXT_MOVE[a].pts,
    )[0];
  }
  if (!pick) return null;
  const m = NEXT_MOVE[pick];
  const pts =
    breakdown[pick] === null
      ? m.pts
      : Math.max(1, Math.round((m.pts * (100 - breakdown[pick])) / 100));
  // A roadmap that already exists but has 0% progress shouldn't say "Generate
  // your roadmap" — it's there, the founder just hasn't started executing it.
  const label =
    pick === "roadmap" && breakdown.roadmap !== null
      ? "Start executing your roadmap"
      : m.label;
  return { key: pick, label, hash: m.hash, roadmap: m.roadmap, pts };
}
